import { UniversityListing } from '../types/listings'

/**
 * Hand-curated advertiser listings for specific guides.
 *
 * Most guides pull listings live from the partner API, but a guide with a fixed
 * editorial line-up (like the Christian colleges guide) needs the same schools,
 * in the same order, with copy we control. Keying by guide slug lets a guide opt
 * into a curated set without changing how every other guide works.
 *
 * Each school leads with a DIFFERENT value proposition on purpose — flexibility,
 * transfer credit, adult-friendly pacing, career focus — so the four read as a
 * comparison rather than four versions of the same pitch. Claims are drawn from
 * each university's own published materials; no ratings, student counts, or
 * outcome figures are invented, and variable claims carry an asterisk covered by
 * the disclosure below the listings.
 */

/**
 * Advertiser click-out. Each curated listing sends the visitor straight to the
 * partner network's redirect endpoint for that school's brand id, so the click
 * is attributed and the visitor lands on the school's own enrollment flow.
 * Everything except the brand id is identical across the five, so it is written
 * once here rather than repeated in every entry.
 */
const BRAND_CLICK_BASE =
  'https://api.media-matchers.com/search?highestEducation=1&gradYear=2018&startTimeframe=1&mPubID=9726577&mPubCampaignID=9999256&redirect=1'
function brandClickUrl(mBrandID: string): string {
  return `${BRAND_CLICK_BASE}&mBrandID=${mBrandID}`
}

const christianColleges: UniversityListing[] = [
  {
    id: 'gcu',
    rank: 1,
    schoolName: 'Grand Canyon University',
    location: 'Phoenix, AZ',
    accreditation: 'HLC Accredited Since 1968',
    topBadge: 'Private Christian University',
    headline: 'Accepting New Enrollments for Online Degree Programs',
    stats: [
      { value: '285+', label: 'Online Programs*' },
      { value: 'Weekly', label: 'Class Starts*' },
      { value: 'No SAT', label: 'or ACT Required' },
    ],
    benefits: [
      'GCU offers 300+ academic programs with an academically rich, values-based curriculum.',
      'Financial aid is available, and the aid team will help create a plan that works for you.',
      'No SAT or ACT scores required to apply.',
    ],
    programs: [
      'Business & Management',
      'Nursing & Health Care',
      'Psychology & Counseling',
      'Teaching & Administration',
      'Theology & Ministry',
    ],
    logo: 'https://cdn.magicpatterns.com/uploads/xsJVVjZgyaxav48XFf9vFt/image.png',
    ctaText: 'Explore GCU Programs',
    ctaUrl: brandClickUrl('90840'),
    ctaSubtext: 'Online Degree Options',
    featured: true,
  },
  {
    id: 'liberty',
    rank: 2,
    schoolName: 'Liberty University',
    location: 'Lynchburg, VA',
    accreditation: 'SACSCOC Accredited',
    topBadge: 'Transfer-Friendly Online University',
    headline:
      'Turn Your Previous College Credits Into Progress Toward a Degree',
    stats: [
      { value: '700+', label: 'Programs of Study' },
      { value: 'Up to 90', label: 'Transfer Credits*' },
      { value: '100%', label: 'Online Options' },
    ],
    benefits: [
      'Choose from hundreds of online degree and certificate options.',
      "Transfer eligible previous college credits toward many bachelor's degree programs.",
      'Get access to academic advisors and online student support throughout your program.',
    ],
    programs: [
      'Business',
      'Healthcare',
      'Psychology',
      'Education',
      'Criminal Justice',
    ],
    logo: 'https://cdn.magicpatterns.com/uploads/6uDWdzBkVowba3NaLBWGNn/image.png',
    ctaText: 'Explore Liberty Degrees',
    ctaUrl: brandClickUrl('11129'),
    ctaSubtext: 'Transfer-Friendly Options',
  },
  {
    id: 'regent',
    rank: 3,
    schoolName: 'Regent University',
    location: 'Virginia Beach, VA',
    accreditation: 'SACSCOC Accredited',
    topBadge: 'Flexible Online Education',
    headline: 'Flexible Online Degrees Built Around Your Life',
    stats: [
      { value: '150+', label: 'Areas of Study' },
      { value: '100%', label: 'Online Options' },
      { value: 'Aid', label: 'Available*' },
    ],
    benefits: [
      'Choose from online undergraduate, graduate, and doctoral degree options.',
      'Study on a flexible schedule designed around work, family, and other commitments.',
      'Get access to academic advising, student support, and financial aid resources.',
    ],
    programs: [
      'Business & Leadership',
      'Psychology & Counseling',
      'Education',
      'Healthcare',
      'Theology',
    ],
    logo: 'https://cdn.magicpatterns.com/uploads/fRfvgbPRrNfay4qxxVYfFL/image.png',
    ctaText: 'See Regent Programs',
    ctaUrl: brandClickUrl('11144'),
    ctaSubtext: '100% Online Options',
  },
  {
    id: 'ccu',
    rank: 4,
    schoolName: 'Colorado Christian University',
    location: 'Lakewood, CO',
    accreditation: 'HLC Accredited',
    topBadge: 'Designed for Working Adults',
    headline: 'Online Degrees Designed for Busy Adults',
    stats: [
      { value: '5-Week', label: 'Course Blocks*' },
      { value: 'Up to 90', label: 'Transfer Credits*' },
      { value: '100%', label: 'Online Degrees' },
    ],
    benefits: [
      'Take online courses designed around the schedules of working adults.',
      'Complete many courses one at a time in focused course blocks.',
      'Transfer eligible college credits and potentially reduce the time needed to finish.',
    ],
    programs: [
      'Business',
      'Education',
      'Psychology',
      'Healthcare',
      'Biblical Studies',
    ],
    logo: 'https://cdn.magicpatterns.com/uploads/nc8K8iB6mzkHyD94rxkx3g/image.png',
    ctaText: 'Explore CCU Online',
    ctaUrl: brandClickUrl('11169'),
    ctaSubtext: 'Flexible Online Learning',
  },
  {
    id: 'hcu',
    rank: 5,
    schoolName: 'Houston Christian University',
    location: 'Houston, TX',
    accreditation: 'SACSCOC Accredited',
    topBadge: 'Career-Focused Christian University',
    headline: 'Career-Focused Online Degrees With a Christian Foundation',
    stats: [
      { value: '100%', label: 'Online Programs' },
      { value: 'SACSCOC', label: 'Accredited' },
      { value: 'Career', label: 'Focused Degrees' },
    ],
    benefits: [
      "Explore online programs designed for today's evolving careers.",
      'Choose from fields including business, education, psychology, technology, and more.',
      'Study in an academically rigorous environment grounded in Christian values.',
    ],
    programs: [
      'Business',
      'Psychology',
      'Education',
      'Cybersecurity',
      'Artificial Intelligence',
    ],
    logo: 'https://cdn.magicpatterns.com/uploads/crisFrw8RcXYHcEgeCYEqj/image.png',
    ctaText: 'Explore HCU Programs',
    ctaUrl: brandClickUrl('11239'),
    ctaSubtext: 'Online Degree Options',
  },
]

const listingsByGuideSlug: Record<string, UniversityListing[]> = {
  'online-christian-colleges': christianColleges,
}

/** Shown below curated listings; covers the asterisked stat claims. */
export const listingDisclosure =
  'Program availability, transfer-credit policies, financial aid eligibility, course length, and online availability vary by program and student. Contact the school for current details.'

export function getStaticGuideListings(
  slug: string | undefined,
): UniversityListing[] | undefined {
  return slug ? listingsByGuideSlug[slug] : undefined
}
